import axios from 'axios'
import type { AxiosInstance, AxiosRequestConfig, AxiosResponse, CancelTokenSource } from 'axios'
import type { ApiResponse, RequestConfig, RequestCanceller } from './types'
import { tokenRefreshManager } from '@/main'
import { clearAllAuthState, isTokenExpired } from '@/utils/authUtils'
import { apiConfigManager } from './config'

// 请求管理器类
class RequestManager {
  private pendingRequests = new Map<string, AbortController>()
  private retryCount = new Map<string, number>()

  // 生成请求唯一标识
  private generateRequestKey(config: AxiosRequestConfig): string {
    const { method, url, params, data } = config
    return `${method?.toUpperCase()}_${url}_${JSON.stringify(params)}_${JSON.stringify(data)}`
  }

  // 添加请求到待处理列表
  addRequest(config: AxiosRequestConfig): AbortController {
    const key = this.generateRequestKey(config)
    const controller = new AbortController()
    
    // 不自动取消重复请求，让所有请求都完成
    this.pendingRequests.set(key, controller)
    return controller
  }

  // 从待处理列表移除请求
  removeRequest(config: AxiosRequestConfig): void {
    const key = this.generateRequestKey(config)
    this.pendingRequests.delete(key)
    this.retryCount.delete(key)
  }

  // 取消所有请求
  cancelAllRequests(): void {
    this.pendingRequests.forEach(controller => controller.abort())
    this.pendingRequests.clear()
    this.retryCount.clear()
  }

  // 取消指定请求
  cancelRequest(config: AxiosRequestConfig): boolean {
    const key = this.generateRequestKey(config)
    const controller = this.pendingRequests.get(key)
    if (controller) {
      controller.abort()
      this.pendingRequests.delete(key)
      this.retryCount.delete(key)
      return true
    }
    return false
  }

  // 获取重试次数
  getRetryCount(config: AxiosRequestConfig): number {
    const key = this.generateRequestKey(config)
    return this.retryCount.get(key) || 0
  }

  // 增加重试次数
  incrementRetryCount(config: AxiosRequestConfig): void {
    const key = this.generateRequestKey(config)
    const count = this.getRetryCount(config) + 1
    this.retryCount.set(key, count)
  }

  // 检查是否应该重试
  shouldRetry(config: AxiosRequestConfig): boolean {
    const configManager = apiConfigManager.getConfig()
    
    // 如果全局禁用重试，直接返回false
    if (!configManager.enableAutoRetry) {
      return false
    }
    
    // 如果请求配置中明确禁用重试，返回false
    if ((config as any).disableRetry === true) {
      return false
    }
    
    const retryCount = this.getRetryCount(config)
    return retryCount < configManager.maxRetries
  }

  // 获取重试延迟时间
  getRetryDelay(config: AxiosRequestConfig): number {
    const configManager = apiConfigManager.getConfig()
    const retryCount = this.getRetryCount(config)
    return configManager.retryDelay * (retryCount + 1)
  }
}

// 创建请求管理器实例
const requestManager = new RequestManager()

// 基础请求类
export class ApiRequest {
  private axiosInstance: AxiosInstance
  private baseURL: string
  private tokenKey: string
  private refreshTokenKey: string

  constructor(baseURL: string, tokenKey: string = 'token') {
    this.baseURL = baseURL
    this.tokenKey = tokenKey
    this.refreshTokenKey = `${tokenKey}_refresh` // 添加 refreshToken 的 key

    const config = apiConfigManager.getConfig()
    
    this.axiosInstance = axios.create({
      baseURL: config.baseURL,
      timeout: config.timeout,
      headers: {
        'Content-Type': 'application/json',
      },
    })

    this.setupInterceptors()
  }

  // 设置拦截器
  private setupInterceptors(): void {
    // 请求拦截器
    this.axiosInstance.interceptors.request.use(
      async (config) => {
        // 检查token是否存在且有效
        let token = this.getToken()
        
        // 如果token不存在或已过期，但有refreshToken，尝试刷新
        if (!token || isTokenExpired(token)) {
          const refreshToken = this.getRefreshToken()
          if (refreshToken) {
            try {
              const newToken = await this.refreshAccessToken(refreshToken)
              if (newToken) {
                token = newToken
              } else {
                // 刷新失败，清除所有认证状态
                this.clearAllAuthState()
              }
            } catch (error) {
              console.error('❌ Token刷新过程中出错:', error)
              this.clearAllAuthState()
            }
          }
        }
        
        // 添加token到请求头
        if (token) {
          config.headers.Authorization = `Bearer ${token}`
        }

        // 添加取消控制器
        const controller = requestManager.addRequest(config)
        config.signal = controller.signal

        return config
      },
      (error) => {
        return Promise.reject(error)
      }
    )

    // 响应拦截器
    this.axiosInstance.interceptors.response.use(
      (response) => {
        // 移除请求记录
        requestManager.removeRequest(response.config)
        return response
      },
      async (error) => {
        // 移除请求记录
        if (error.config) {
          requestManager.removeRequest(error.config)
        }

        // 处理401错误 - 尝试自动刷新token
        if (error.response?.status === 401) {
          const refreshToken = this.getRefreshToken()
          if (refreshToken) {
            try {
              // 尝试刷新token
              const refreshResponse = await this.refreshAccessToken(refreshToken)
              if (refreshResponse) {
                // 刷新成功，重试原请求
                const originalRequest = error.config
                originalRequest.headers.Authorization = `Bearer ${refreshResponse}`
                return this.axiosInstance.request(originalRequest)
              } else {
                // 刷新失败时，认证状态已在 performRefreshToken 中清除
                return Promise.reject(error)
              }
            } catch (refreshError) {
              console.error('❌ 刷新token失败:', refreshError)
              // 刷新过程中出错，认证状态已在 performRefreshToken 中清除
              return Promise.reject(error)
            }
          } else {
            // 没有refreshToken时清除所有认证状态
            this.clearAllAuthState()
            return Promise.reject(error)
          }
        }

        // 处理网络错误和超时
        if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
          // 根据配置决定是否进行自动重试
          if (requestManager.shouldRetry(error.config)) {
            requestManager.incrementRetryCount(error.config)
            await this.delay(requestManager.getRetryDelay(error.config))
            return this.axiosInstance.request(error.config)
          } else {
            // 自动重试已禁用，直接抛出错误
            console.warn('请求超时或连接中断，自动重试已禁用:', error.message)
          }
        }

        return Promise.reject(error)
      }
    )
  }

  // 延迟函数
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms))
  }

  // 创建请求取消器
  createCanceller(): RequestCanceller {
    let cancelled = false
    return {
      cancel: () => {
        cancelled = true
      },
      isCancelled: () => cancelled
    }
  }

  // 通用请求方法
  async request<T = any>(
    config: AxiosRequestConfig,
    requestConfig?: RequestConfig
  ): Promise<ApiResponse<T>> {
    try {
      // 将RequestConfig中的disableRetry属性传递给axios配置
      if (requestConfig?.disableRetry) {
        (config as any).disableRetry = true
      }
      
      // 检查是否已取消
      if (requestConfig?.cancelToken?.signal.aborted) {
        throw new Error('请求已取消')
      }

      const response: AxiosResponse<ApiResponse<T>> = await this.axiosInstance.request(config)
      
      // 检查是否已取消
      if (requestConfig?.cancelToken?.signal.aborted) {
        throw new Error('请求已取消')
      }

      return response.data
    } catch (error: any) {
      // 如果是取消请求，直接抛出
      if (axios.isCancel(error) || error.message === '请求已取消') {
        throw error
      }

      // 处理错误响应
      const errorMessage = error.response?.data?.message || error.message || '请求失败'
      
      if (requestConfig?.showError !== false) {
        console.error('API请求错误:', errorMessage)
      }

      throw new Error(errorMessage)
    }
  }

  // GET请求
  async get<T = any>(
    url: string,
    params?: any,
    requestConfig?: RequestConfig
  ): Promise<ApiResponse<T>> {
    return this.request<T>(
      {
        method: 'GET',
        url,
        params,
      },
      requestConfig
    )
  }

  // POST请求
  async post<T = any>(
    url: string,
    data?: any,
    requestConfig?: RequestConfig
  ): Promise<ApiResponse<T>> {
    return this.request<T>(
      {
        method: 'POST',
        url,
        data,
      },
      requestConfig
    )
  }

  // PUT请求
  async put<T = any>(
    url: string,
    data?: any,
    requestConfig?: RequestConfig
  ): Promise<ApiResponse<T>> {
    return this.request<T>(
      {
        method: 'PUT',
        url,
        data,
      },
      requestConfig
    )
  }

  // DELETE请求
  async delete<T = any>(
    url: string,
    requestConfig?: RequestConfig
  ): Promise<ApiResponse<T>> {
    return this.request<T>(
      {
        method: 'DELETE',
        url,
      },
      requestConfig
    )
  }

  // 取消所有请求
  cancelAllRequests(): void {
    requestManager.cancelAllRequests()
  }

  // 取消指定请求
  cancelRequest(config: AxiosRequestConfig): boolean {
    return requestManager.cancelRequest(config)
  }

  // 设置token
  setToken(token: string): void {
    localStorage.setItem(this.tokenKey, token)
  }

  // 设置refreshToken
  setRefreshToken(refreshToken: string): void {
    localStorage.setItem(this.refreshTokenKey, refreshToken)
  }

  // 获取token
  getToken(): string | null {
    return localStorage.getItem(this.tokenKey)
  }

  // 获取refreshToken
  getRefreshToken(): string | null {
    return localStorage.getItem(this.refreshTokenKey)
  }

  // 清除token
  clearToken(): void {
    // 在开发环境中，跳过清除操作以保持localStorage数据
    if (import.meta.env.DEV) {
      console.log('开发环境：跳过清除token操作，保持localStorage数据')
      return
    }
    localStorage.removeItem(this.tokenKey)
    localStorage.removeItem(this.refreshTokenKey) // 同时清除 refreshToken
  }

  // 清除所有认证状态（包括用户信息）
  clearAllAuthState(): void {
    clearAllAuthState('请求拦截器')
  }

  // 刷新访问令牌（带防重复调用保护）
  async refreshAccessToken(refreshToken: string): Promise<string | null> {
    // 如果正在刷新，返回现有的promise
    if (tokenRefreshManager.isRefreshing() && tokenRefreshManager.getRefreshPromise()) {
      return tokenRefreshManager.getRefreshPromise()
    }
    
    // 开始刷新
    tokenRefreshManager.setRefreshing(true)
    const refreshPromise = this.performRefreshToken(refreshToken)
    tokenRefreshManager.setRefreshPromise(refreshPromise)
    
    try {
      const result = await refreshPromise
      return result
    } finally {
      // 刷新完成，重置状态
      tokenRefreshManager.setRefreshing(false)
      tokenRefreshManager.setRefreshPromise(null)
    }
  }

  // 执行实际的刷新操作
  private async performRefreshToken(refreshToken: string): Promise<string | null> {
    try {
      const response = await this.axiosInstance.post('/api/auth/refresh', {
        refreshToken: refreshToken
      })
      
      if (response.data.success && response.data.data) {
        const newAccessToken = response.data.data
        this.setToken(newAccessToken)
        return newAccessToken
      } else {
        // 刷新失败时清除所有认证状态
        this.clearAllAuthState()
        return null
      }
    } catch (error) {
      console.error('❌ 刷新访问令牌失败:', error)
      // 刷新失败时清除所有认证状态
      this.clearAllAuthState()
      return null
    }
  }

  // 更新baseURL
  updateBaseURL(baseURL: string): void {
    this.baseURL = baseURL
    this.axiosInstance.defaults.baseURL = baseURL
  }


}

// 创建默认的API请求实例
export const apiRequest = new ApiRequest(
  import.meta.env.VITE_API_BASE_URL,
  'platformToken'
)

// 导出请求管理器
export { requestManager } 